import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@shared/api";
import { Banner, Field } from "@shared/ui";

export interface MediaRow {
  id: string;
  kind: "image" | "video";
  mimeType: string;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  source: string;
  prompt: string | null;
  altText: string | null;
  status: string;
  previewUrl: string;
  publicUrl: string | null;
  createdAt: number;
}

/** Anything over this goes up in parts, because a Worker will not take it whole. */
const PART_SIZE = 20 * 1024 * 1024;
const SINGLE_REQUEST_LIMIT = 90 * 1024 * 1024;

export function MediaPicker({
  workspaceId,
  selected,
  suggestedPrompt,
  platform,
  onChange,
}: {
  workspaceId: string;
  selected: string[];
  suggestedPrompt?: string;
  platform: string;
  onChange: (mediaIds: string[]) => Promise<void> | void;
}) {
  const [library, setLibrary] = useState<MediaRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [prompt, setPrompt] = useState(suggestedPrompt ?? "");
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ media: MediaRow[] }>(
        `/api/admin/media?workspaceId=${workspaceId}`,
      );
      setLibrary(data.media);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the media library");
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(id: string) {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    await onChange(next);
  }

  async function handleFile(file: File) {
    setError(null);
    setBusy("upload");
    setProgress(0);
    try {
      const media =
        file.size > SINGLE_REQUEST_LIMIT
          ? await uploadInParts(workspaceId, file, setProgress)
          : await uploadWhole(workspaceId, file);
      await load();
      await onChange([...selected, media.id]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That upload did not work");
    } finally {
      setBusy(null);
      setProgress(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function generate() {
    setError(null);
    setBusy("generate");
    try {
      const res = await api.post<{ media: MediaRow }>("/api/admin/media/generate", {
        workspaceId,
        prompt,
        aspectRatio: platform === "instagram" ? "4:5" : platform === "x" ? "16:9" : "1:1",
      });
      await load();
      await onChange([...selected, res.media.id]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not generate an image");
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this file? It will be removed from any post using it.")) return;
    await api.delete(`/api/admin/media/${id}`);
    await load();
    await onChange(selected.filter((x) => x !== id));
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h3 style={{ marginBottom: 10 }}>Images and video</h3>
      {error ? <Banner kind="error">{error}</Banner> : null}

      {selected.length > 0 && library ? (
        <div className="media-strip" style={{ marginBottom: 14 }}>
          {selected.map((id) => {
            const item = library.find((m) => m.id === id);
            if (!item) return null;
            return (
              <figure key={id} className="media-tile selected">
                <MediaThumb item={item} />
                <button className="small" onClick={() => void toggle(id)}>
                  Remove
                </button>
              </figure>
            );
          })}
        </div>
      ) : null}

      <div className="actions" style={{ marginBottom: 14 }}>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <button onClick={() => fileInput.current?.click()} disabled={busy !== null}>
          {busy === "upload"
            ? progress !== null && progress > 0
              ? `Uploading ${progress}%`
              : "Uploading…"
            : "Upload a file"}
        </button>
      </div>

      <Field
        label="Or generate an image"
        hint="Uses whichever AI key on this client can make images."
      >
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A clean industrial workshop with forklifts, daylight, no people"
          style={{ minHeight: 60 }}
        />
      </Field>
      <div className="actions">
        <button onClick={() => void generate()} disabled={busy !== null || prompt.trim().length < 3}>
          {busy === "generate" ? "Generating…" : "Generate"}
        </button>
      </div>

      {library && library.length > 0 ? (
        <>
          <h3 style={{ marginTop: 20, marginBottom: 10 }}>Library</h3>
          <div className="media-strip">
            {library.map((item) => (
              <figure
                key={item.id}
                className={`media-tile ${selected.includes(item.id) ? "selected" : ""}`}
              >
                <button className="media-hit" onClick={() => void toggle(item.id)}>
                  <MediaThumb item={item} />
                </button>
                <figcaption className="muted">
                  {item.source === "ai" ? "AI" : "Upload"}
                  {item.width ? ` · ${item.width}×${item.height}` : ""}
                </figcaption>
                <button className="small danger" onClick={() => void remove(item.id)}>
                  Delete
                </button>
              </figure>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function MediaThumb({ item }: { item: MediaRow }) {
  if (item.kind === "video") {
    return (
      <video
        src={item.previewUrl}
        className="media-thumb"
        muted
        playsInline
        preload="metadata"
      />
    );
  }
  return <img src={item.previewUrl} alt={item.altText ?? ""} className="media-thumb" />;
}

/* ------------------------------------------------------------------ */

async function uploadWhole(workspaceId: string, file: File): Promise<MediaRow> {
  const form = new FormData();
  form.append("workspaceId", workspaceId);
  form.append("file", file);

  const res = await fetch("/api/admin/media", { method: "POST", body: form });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error ?? "Upload failed");
  return (body as { media: MediaRow }).media;
}

/**
 * Large video, sent to R2 in parts. A Worker request body cannot hold a whole
 * video, so the file is sliced here and reassembled by R2 on completion.
 */
async function uploadInParts(
  workspaceId: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<MediaRow> {
  const start = await api.post<{ mediaId: string; uploadId: string }>(
    "/api/admin/media/multipart/start",
    { workspaceId, mimeType: file.type, sizeBytes: file.size },
  );

  const parts: { partNumber: number; etag: string }[] = [];
  const total = Math.ceil(file.size / PART_SIZE);

  try {
    for (let i = 0; i < total; i++) {
      const slice = file.slice(i * PART_SIZE, (i + 1) * PART_SIZE);
      const res = await fetch(
        `/api/admin/media/multipart/part?mediaId=${start.mediaId}&uploadId=${encodeURIComponent(start.uploadId)}&partNumber=${i + 1}`,
        { method: "PUT", body: slice },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Part ${i + 1} failed`);
      }
      parts.push(await res.json());
      onProgress(Math.round(((i + 1) / total) * 100));
    }

    const done = await api.post<{ media: MediaRow }>("/api/admin/media/multipart/complete", {
      mediaId: start.mediaId,
      uploadId: start.uploadId,
      parts,
    });
    return done.media;
  } catch (err) {
    // Leave no half-finished upload behind, in storage or in the library.
    await api
      .post("/api/admin/media/multipart/abort", {
        mediaId: start.mediaId,
        uploadId: start.uploadId,
      })
      .catch(() => {});
    throw err;
  }
}
